import React, { useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'

import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track } from 'modules/core/lib/Tracking'

import type {
  ResultType,
  SearchPluginHookData,
} from 'modules/command_bar/components/CommandBar/plugins/types'
import { useCommandBar } from 'modules/command_bar/lib/commandBarContext'

import EmptyState from './components/EmptyState'
import LoadingState from './components/LoadingState'
import Result from './components/Result'
import SectionTitle from './components/SectionTitle'
import { ResultRefs, SelectedResult, SelectionType } from './types'

interface Props {
  plugins: SearchPluginHookData[]
  loading: boolean
  search: string | null
}

const Results = ({ plugins, loading, search }: Props) => {
  const results = useUnsafeMemo(() => plugins.flatMap((plugin) => plugin.results), [plugins])
  const [selected, setSelected] = useState<SelectedResult>({
    id: results[0]?.id,
    type: SelectionType.Auto,
  })
  const navigate = useNavigate()
  const { setOpen } = useCommandBar()
  const elmRefs = useRef<ResultRefs>({})

  useUnsafeEffect(() => {
    results.forEach((result) => {
      elmRefs.current[result.id] = { current: null }
    })
  }, [results])

  useLayoutEffect(() => {
    if (selected.id && selected.type === SelectionType.Keyboard) {
      const elementRef = elmRefs?.current?.[selected.id]?.current
      elementRef?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selected.id, selected.type])

  const onSubmit = useUnsafeCallback(
    (selectedId) => {
      const result = results.find(({ id }) => id === selectedId)

      if (result) {
        track('Create search Success', {
          search_keyword: search,
          search_result_type: result.type,
          search_result_action: result.type === 'action' && result.id,
          search_result_page_path:
            result.type === 'navigation' && !result.external && result.navigationPath,
          search_result_external_page_url:
            result.type === 'navigation' && result.external && result.navigationPath,
        })
      }
    },
    [search, results]
  )

  useUnsafeEffect(() => {
    const selectionDefaults = { type: SelectionType.Keyboard }

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentSelectionIndex = results.findIndex(
        (result: ResultType) => selected.id === result.id
      )

      if (event.code === 'ArrowUp' || (event.code === 'Tab' && event.shiftKey)) {
        event.preventDefault()

        const nextIndex = currentSelectionIndex - 1
        nextIndex >= 0 && setSelected({ id: results[nextIndex]?.id, ...selectionDefaults })
      }

      if (event.code === 'ArrowDown' || (event.code === 'Tab' && !event.shiftKey)) {
        event.preventDefault()

        if (selected) {
          const nextIndex = currentSelectionIndex + 1
          nextIndex < results.length &&
            setSelected({ id: results[nextIndex]?.id, ...selectionDefaults })
        } else {
          setSelected({ id: results[0]?.id, ...selectionDefaults })
        }
      }

      if (event.code === 'Enter') {
        event.preventDefault()

        const result = results[currentSelectionIndex]

        if (result && result.type === 'navigation' && !result.external) {
          setOpen?.(false)
        }

        onSubmit(selected.id)

        if (result) {
          if (result.type === 'action') {
            result.action()
            setOpen(false)
          } else {
            result.external
              ? window.open(result.navigationPath, '_blank')
              : navigate(`${result.navigationPath}?command_bar=true`)
          }
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [navigate, results, selected, setOpen, onSubmit])

  useUnsafeEffect(() => {
    if (results[0]?.id !== selected.id) {
      setSelected({ id: results[0]?.id, type: SelectionType.Auto })
    }
  }, [results[0]?.id])

  const onMouseEnter = useCallback((resultId: string) => {
    setSelected({ id: resultId, type: SelectionType.Cursor })
  }, [])

  useEffect(() => {
    if (!loading && !!search) {
      results.length > 0
        ? track('Create search Submit', { search_keyword: search })
        : track('Create search Empty', { search_keyword: search })
    }
  }, [loading])

  if (!results.length && loading) return <LoadingState />

  if (!results.length) return <EmptyState />

  return (
    <>
      {plugins.map((plugin) => {
        if (!plugin.results.length) return null

        return (
          <Box gap="s4" key={plugin.id}>
            {plugin.title && (
              <Box paddingLeft="s12">
                <SectionTitle title={plugin.title} />
              </Box>
            )}
            <Box>
              {plugin.results.map((result, index) => {
                // This is to make the keyboard navigation reach the end of the list properly when scrolling to the last item,
                // applying the padding to the last element instead of the list block. Otherwise, with no padding on the last element,
                // it leaves the scroll position too tight to the Help Bar, with the list padding below it, yet to be scrolled.
                const isLastResult = index === plugin.results.length - 1
                const elementPaddingBottom = isLastResult ? 's16' : undefined

                return (
                  <Box
                    ref={elmRefs.current[result.id]}
                    paddingBottom={elementPaddingBottom}
                    key={result.id}
                  >
                    <Result
                      {...result}
                      selected={selected.id === result.id}
                      onMouseEnter={onMouseEnter}
                      onSubmit={onSubmit}
                    />
                  </Box>
                )
              })}
            </Box>
          </Box>
        )
      })}
    </>
  )
}

export default Results
